import { WrapperWidget } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { BarChart } from '~/components/charts/BarChart';

interface RejectionData {
  readonly reason: string;
  readonly count: number;
  readonly percentage: number;
}

interface Props {
  readonly title?: string;
  readonly data?: RejectionData[];
  readonly infoText?: string;
}

export function CockpitRejectionReasonsGraphWidget(props: Props) {
  const { t } = useTranslate();

  // Ensure data is always an array
  const dataArray = props.data || [];

  if (!dataArray.length) {
    return (
      <WrapperWidget
        title={t(props.title || 'cockpit_rejection_reasons_graph_title')}
      >
        <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
          {t('cockpit_rejection_reasons_no_data')}
        </div>
      </WrapperWidget>
    );
  }

  // Sort by count (highest first)
  const sortedData = [...dataArray].sort((a, b) => b.count - a.count);

  // Debug logging
  console.log('Rejection reasons data:', dataArray);
  console.log('Sorted data:', sortedData);

  // Prepare chart data
  const chartData = sortedData.map((item) => ({
    x: (t(`rejection_reason_${item.reason}`) || item.reason).substring(0, 20),
    y: item.count,
  }));

  console.log('Chart data for BarChart:', chartData);

  return (
    <WrapperWidget
      title={t(props.title || 'cockpit_rejection_reasons_graph_title')}
      text={
        props.infoText ? t(props.infoText as any) || props.infoText : undefined
      }
    >
      <div className="h-full flex flex-col gap-y-4">
        {/* Bar Chart */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 h-80">
          <BarChart
            datasetIdKey={'rejection-reasons'}
            series={chartData}
            yFormat="number"
            xFormat="string"
          />
        </div>

        {/* Top reasons list */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sortedData.slice(0, 6).map((item, index) => (
            <div
              key={item.reason}
              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 dark:bg-gray-900 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    index === 0
                      ? 'bg-red-500'
                      : index === 1
                      ? 'bg-orange-500'
                      : index === 2
                      ? 'bg-yellow-500'
                      : 'bg-gray-400'
                  }`}
                />
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {t(`rejection_reason_${item.reason}`) || item.reason}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-700 dark:text-slate-300 tabular-nums">
                  {item.count}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                  ({item.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </WrapperWidget>
  );
}
